import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { useRaceStore } from '../../stores/raceStore';
import { useTranslation } from '../../utils/i18n';
import { NutritionIntakeItem, NutritionProduct, SegmentNutritionOutput, NutritionState } from '../../types';
import { computeSegmentNutrition, computeGlobalNutritionState, getNutritionStateLabel } from '../../utils/nutritionEngine';
import { trackNutritionSegmentOpened, trackNutritionProductAdded } from '../../utils/analytics';
import { UtensilsIcon, ShoppingBagIcon, CheckCircle2Icon, AlertCircleIcon, CircleDotIcon, PlusIcon, MinusIcon, XIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon, DropletIcon, TimerIcon, ClockIcon, ZapIcon, FlameIcon, MilkIcon } from 'lucide-react';
// --- Constants & Helpers ---
const STATE_COLORS = {
  not_planned: {
    bg: 'bg-[#F4F4F5]',
    text: 'text-[#71717A]',
    border: 'border-[#E4E4E7]',
    dot: 'bg-[#A1A1AA]',
    icon: CircleDotIcon
  },
  needs_adjustment: {
    bg: 'bg-[#FFFBEB]',
    text: 'text-[#B45309]',
    border: 'border-[#FCD34D]',
    dot: 'bg-[#F59E0B]',
    icon: AlertCircleIcon
  },
  on_target: {
    bg: 'bg-[#ECFDF5]',
    text: 'text-[#047857]',
    border: 'border-[#6EE7B7]',
    dot: 'bg-[#10B981]',
    icon: CheckCircle2Icon
  }
};
function formatDuration(mins: number) {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}
// --- Shared UI Components ---
function StatusBadge({
  state
}: {
  state: NutritionState;
}) {
  const {
    t
  } = useTranslation();
  const config = STATE_COLORS[state];
  const Icon = config.icon;
  return <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${config.bg} ${config.text} ${config.border}`} data-id="element-529">
      <Icon className="w-3.5 h-3.5" data-id="element-530" />
      <span data-id="element-531">{getNutritionStateLabel(state, t)}</span>
    </div>;
}
function ProgressBar({
  value,
  max,
  colorClass,
  height = 'h-1.5'
}: {
  value: number;
  max: number;
  colorClass: string;
  height?: string;
}) {
  const pct = max > 0 ? Math.min(value / max * 100, 100) : 0;
  return <div className={`w-full bg-[#F0F0F5] rounded-full overflow-hidden ${height}`} data-id="element-532">
      <div className={`h-full rounded-full transition-all duration-500 ${colorClass}`} style={{
      width: `${pct}%`
    }} data-id="element-533" />
    </div>;
}
// --- Inline Detail Components ---
function ProductSearch({
  availableProducts,
  onAdd
}: {
  availableProducts: NutritionProduct[];
  onAdd: (productId: string) => void;
}) {
  const {
    t
  } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const filtered = useMemo(() => {
    if (!query.trim()) return availableProducts;
    const q = query.toLowerCase();
    return availableProducts.filter(p => p.name.toLowerCase().includes(q) || p.unitLabel.toLowerCase().includes(q));
  }, [availableProducts, query]);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return <div ref={ref} className="relative w-full" data-id="element-534">
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#F7F7FA] border border-[#E8E8ED] rounded-xl focus-within:ring-2 focus-within:ring-[#FC4C02] focus-within:border-transparent transition-all" data-id="element-535">
        <SearchIcon className="w-4 h-4 text-[#9B9BA7]" data-id="element-536" />
        <input type="text" placeholder={t('nutrition.searchProduct')} className="flex-1 bg-transparent border-none p-0 text-sm placeholder:text-[#9B9BA7] focus:ring-0" value={query} onChange={e => {
        setQuery(e.target.value);
        setOpen(true);
      }} onFocus={() => setOpen(true)} data-id="element-537" />
      </div>

      {open && filtered.length > 0 && <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-[#E8E8ED] rounded-xl shadow-xl max-h-60 overflow-y-auto z-50 divide-y divide-[#F0F0F5]" data-id="element-538">
          {filtered.map(p => <button key={p.id} onClick={() => {
        onAdd(p.id);
        setQuery('');
        setOpen(false);
      }} className="w-full text-left px-4 py-3 hover:bg-[#F7F7FA] transition-colors flex items-center justify-between group" data-id="element-539">
              <div data-id="element-540">
                <div className="font-medium text-[#2D2D2D] text-sm" data-id="element-541">
                  {p.name}
                </div>
                <div className="text-xs text-[#9B9BA7] mt-0.5" data-id="element-542">
                  {p.carbsPerUnitG}g CHO · {p.caloriesPerUnitKcal} kcal ·{' '}
                  {p.sodiumPerUnitMg}mg Na+
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-[#F7F7FA] text-[#FC4C02] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-543">
                <PlusIcon className="w-5 h-5" data-id="element-544" />
              </div>
            </button>)}
        </div>}
    </div>;
}
function ProductControl({
  item,
  product,
  onChange,
  onRemove
}: {
  item: NutritionIntakeItem;
  product: NutritionProduct;
  onChange: (qty: number) => void;
  onRemove: () => void;
}) {
  return <div className="flex items-center justify-between p-3 bg-white border border-[#E8E8ED] rounded-xl shadow-sm" data-id="element-545">
      <div className="flex-1 min-w-0 mr-4" data-id="element-546">
        <div className="font-semibold text-[#2D2D2D] text-sm truncate" data-id="element-547">
          {product.name}
        </div>
        <div className="text-xs text-[#9B9BA7] mt-0.5" data-id="element-548">
          {Math.round(product.carbsPerUnitG * item.quantity)}g CHO ·{' '}
          {Math.round(product.sodiumPerUnitMg! * item.quantity)}mg Na+
        </div>
      </div>

      <div className="flex items-center gap-3 bg-[#F7F7FA] rounded-lg p-1" data-id="element-549">
        <button onClick={() => item.quantity <= 0.5 ? onRemove() : onChange(item.quantity - 0.5)} className="w-7 h-7 flex items-center justify-center rounded-md bg-white text-[#6B6B76] shadow-sm hover:text-[#E34402] transition-colors" data-id="element-550">
          {item.quantity <= 0.5 ? <XIcon className="w-4 h-4" data-id="element-551" /> : <MinusIcon className="w-4 h-4" data-id="element-552" />}
        </button>
        <span className="w-8 text-center font-bold text-[#2D2D2D] text-sm tabular-nums" data-id="element-553">
          {item.quantity}
        </span>
        <button onClick={() => onChange(item.quantity + 0.5)} className="w-7 h-7 flex items-center justify-center rounded-md bg-white text-[#FC4C02] shadow-sm hover:bg-[#FFF5F0] transition-colors" data-id="element-554">
          <PlusIcon className="w-4 h-4" data-id="element-555" />
        </button>
      </div>
    </div>;
}
// --- Inline Segment Detail Panel (replaces bottom sheet) ---
function SegmentDetailPanel({
  segmentId,
  onClose
}: {
  segmentId: string;
  onClose: () => void;
}) {
  const {
    t
  } = useTranslation();
  const {
    segments,
    pacingResults,
    nutritionPlans,
    products,
    settings,
    updateNutritionPlan
  } = useRaceStore();
  const segment = segments.find(s => s.id === segmentId);
  const pacing = pacingResults.find(p => p.segmentId === segmentId);
  const plan = nutritionPlans[segmentId] || {
    segmentId,
    intakeItems: []
  };
  const output = useMemo(() => {
    if (!segment || !pacing) return null;
    return computeSegmentNutrition(segment, pacing, plan, settings, products);
  }, [segment, pacing, plan, settings, products]);
  if (!segment || !pacing || !output) return null;
  const activeItems = plan.intakeItems.filter(i => i.quantity > 0);
  const activeProductIds = new Set(activeItems.map(i => i.productId));
  const availableProducts = products.filter(p => !activeProductIds.has(p.id));
  const handleQtyChange = (productId: string, qty: number) => {
    let newItems = [...plan.intakeItems];
    const idx = newItems.findIndex(i => i.productId === productId);
    if (idx >= 0) {
      if (qty <= 0) newItems.splice(idx, 1);else newItems[idx] = {
        ...newItems[idx],
        quantity: qty
      };
    } else if (qty > 0) {
      newItems.push({
        productId,
        quantity: qty
      });
      trackNutritionProductAdded({
        product_id: productId,
        segment_id: segmentId
      });
    }
    updateNutritionPlan(segment.id, {
      intakeItems: newItems
    });
  };
  // Green for in-range OR above-target; orange only for below-target
  const getDimColor = (inRange: boolean, aboveTarget?: boolean) => inRange || aboveTarget ? 'text-[#00B843]' : 'text-[#FF9500]';
  const getDimBg = (inRange: boolean, aboveTarget?: boolean) => inRange || aboveTarget ? 'bg-[#00B843]' : 'bg-[#FF9500]';
  const hasAnyAboveTarget = output.carbsAboveTarget || output.caloriesAboveTarget || output.sodiumAboveTarget;
  return <div className="bg-[#FAFAFA] rounded-b-xl border border-t-0 border-[#E8E8ED] -mt-1" data-id="element-556">
      <div className="p-4 space-y-5" data-id="element-557">
        {/* Nutrition Targets */}
        <div className="space-y-3" data-id="element-558">
          {/* Carbs */}
          <div className="space-y-1.5" data-id="element-559">
            <div className="flex justify-between text-sm" data-id="element-560">
              <span className="font-medium text-[#2D2D2D] flex items-center gap-1.5" data-id="element-561">
                <ZapIcon className="w-3.5 h-3.5 text-[#FC4C02]" data-id="element-562" />{' '}
                {t('nutrition.carbs')}
              </span>
              <span className={`font-bold tabular-nums text-xs ${getDimColor(output.carbsInRange, output.carbsAboveTarget)}`} data-id="element-563">
                {Math.round(output.plannedChoG)} /{' '}
                {Math.round(output.targetChoG)}g
              </span>
            </div>
            <ProgressBar value={output.plannedChoG} max={output.targetChoG} colorClass={getDimBg(output.carbsInRange, output.carbsAboveTarget)} data-id="element-564" />
          </div>

          {/* Calories */}
          <div className="space-y-1.5" data-id="element-565">
            <div className="flex justify-between text-sm" data-id="element-566">
              <span className="font-medium text-[#2D2D2D] flex items-center gap-1.5" data-id="element-567">
                <FlameIcon className="w-3.5 h-3.5 text-[#FC4C02]" data-id="element-568" />{' '}
                {t('nutrition.calories')}
              </span>
              <span className={`font-bold tabular-nums text-xs ${getDimColor(output.caloriesInRange, output.caloriesAboveTarget)}`} data-id="element-569">
                {Math.round(output.plannedCalKcal)} /{' '}
                {Math.round(output.targetCalKcal)} kcal
              </span>
            </div>
            <ProgressBar value={output.plannedCalKcal} max={output.targetCalKcal} colorClass={getDimBg(output.caloriesInRange, output.caloriesAboveTarget)} data-id="element-570" />
          </div>

          {/* Sodium */}
          <div className="space-y-1.5" data-id="element-571">
            <div className="flex justify-between text-sm" data-id="element-572">
              <span className="font-medium text-[#2D2D2D] flex items-center gap-1.5" data-id="element-573">
                <MilkIcon className="w-3.5 h-3.5 text-[#FC4C02]" data-id="element-574" />{' '}
                {t('nutrition.sodium')}
              </span>
              <span className={`font-bold tabular-nums text-xs ${getDimColor(output.sodiumInRange, output.sodiumAboveTarget)}`} data-id="element-575">
                {Math.round(output.plannedSodiumMg)} /{' '}
                {Math.round(output.targetSodiumMg)}mg
              </span>
            </div>
            <ProgressBar value={output.plannedSodiumMg} max={output.targetSodiumMg} colorClass={getDimBg(output.sodiumInRange, output.sodiumAboveTarget)} data-id="element-576" />
          </div>
        </div>

        {/* Above Target Info Note */}
        {hasAnyAboveTarget && <div className="p-3 bg-[#FFFBEB] border border-[#FEF3C7] rounded-xl flex items-start gap-2.5" data-id="element-577">
            <AlertCircleIcon className="w-4 h-4 text-[#D97706] mt-0.5 flex-shrink-0" data-id="element-578" />
            <div className="text-xs text-[#92400E] leading-relaxed" data-id="element-579">
              {t('nutrition.aboveTargetNote') || 'Planned intake is above the recommended target. This is fine for shorter segments or personal preference — guidance only.'}
            </div>
          </div>}

        {/* Fluid Recommendation */}
        <div className="p-3 bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl flex items-start gap-2.5" data-id="element-580">
          <DropletIcon className="w-4 h-4 text-[#0284C7] mt-0.5 flex-shrink-0" data-id="element-581" />
          <div className="text-xs text-[#0284C7] leading-relaxed" data-id="element-582">
            {t('nutrition.fluidGuidance', {
            amount: Math.round(output.recommendedFluidMl).toString()
          })}
          </div>
        </div>

        {/* Products */}
        <div className="space-y-3" data-id="element-583">
          <h4 className="text-[10px] font-bold text-[#9B9BA7] uppercase tracking-wider" data-id="element-584">
            {t('nutrition.plannedProducts')}
          </h4>

          {activeItems.map(item => {
          const product = products.find(p => p.id === item.productId);
          if (!product) return null;
          return <ProductControl key={item.productId} item={item} product={product} onChange={qty => handleQtyChange(item.productId, qty)} onRemove={() => handleQtyChange(item.productId, 0)} data-id="element-585" />;
        })}

          {activeItems.length === 0 && <div className="text-center py-4 border-2 border-dashed border-[#E8E8ED] rounded-xl text-[#9B9BA7] text-xs bg-white" data-id="element-586">
              {t('nutrition.noProductsYet')}
            </div>}

          <ProductSearch availableProducts={availableProducts} onAdd={id => handleQtyChange(id, 1)} data-id="element-587" />
        </div>
      </div>
    </div>;
}
// --- Layer 1 & 2: Main View Components ---
function GlobalSummaryCard({
  state,
  totalPlannedCho,
  totalTargetCho,
  totalPlannedCal,
  totalTargetCal,
  totalPlannedSodium,
  totalTargetSodium,
  totalRecommendedFluid
}: {
  state: NutritionState;
  totalPlannedCho: number;
  totalTargetCho: number;
  totalPlannedCal: number;
  totalTargetCal: number;
  totalPlannedSodium: number;
  totalTargetSodium: number;
  totalRecommendedFluid: number;
}) {
  const {
    t
  } = useTranslation();
  return <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-3 sm:p-4 space-y-3" data-id="element-588">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-id="element-589">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 flex-1 min-w-0" data-id="element-590">
          <div className="space-y-1 sm:space-y-1.5 min-w-0" data-id="element-591">
            <div className="text-[9px] sm:text-[10px] font-bold text-[#9B9BA7] uppercase truncate" data-id="element-592">
              {t('nutrition.carbs')}
            </div>
            <div className="text-sm sm:text-lg font-bold text-[#2D2D2D] tabular-nums leading-none" data-id="element-593">
              {Math.round(totalPlannedCho)}
              <span className="text-[9px] sm:text-xs font-medium text-[#9B9BA7]" data-id="element-594">
                /{Math.round(totalTargetCho)}g
              </span>
            </div>
            <ProgressBar value={totalPlannedCho} max={totalTargetCho} colorClass="bg-[#FC4C02]" height="h-1" data-id="element-595" />
          </div>
          <div className="space-y-1 sm:space-y-1.5 min-w-0" data-id="element-596">
            <div className="text-[9px] sm:text-[10px] font-bold text-[#9B9BA7] uppercase truncate" data-id="element-597">
              {t('nutrition.calories')}
            </div>
            <div className="text-sm sm:text-lg font-bold text-[#2D2D2D] tabular-nums leading-none" data-id="element-598">
              {Math.round(totalPlannedCal)}
              <span className="text-[9px] sm:text-xs font-medium text-[#9B9BA7]" data-id="element-599">
                /{Math.round(totalTargetCal)}
              </span>
            </div>
            <ProgressBar value={totalPlannedCal} max={totalTargetCal} colorClass="bg-[#FC4C02]" height="h-1" data-id="element-600" />
          </div>
          <div className="space-y-1 sm:space-y-1.5 min-w-0" data-id="element-601">
            <div className="text-[9px] sm:text-[10px] font-bold text-[#9B9BA7] uppercase truncate" data-id="element-602">
              {t('nutrition.sodium')}
            </div>
            <div className="text-sm sm:text-lg font-bold text-[#2D2D2D] tabular-nums leading-none" data-id="element-603">
              {Math.round(totalPlannedSodium)}
              <span className="text-[9px] sm:text-xs font-medium text-[#9B9BA7]" data-id="element-604">
                /{Math.round(totalTargetSodium)}
              </span>
            </div>
            <ProgressBar value={totalPlannedSodium} max={totalTargetSodium} colorClass="bg-[#FC4C02]" height="h-1" data-id="element-605" />
          </div>
        </div>
        <div className="flex sm:block justify-end" data-id="element-606">
          <StatusBadge state={state} data-id="element-607" />
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11px] sm:text-xs text-[#0284C7] bg-[#F0F9FF] px-3 py-2 rounded-lg border border-[#BAE6FD]" data-id="element-608">
        <DropletIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" data-id="element-609" />
        <span data-id="element-610">
          {t('nutrition.recommendedFluid')}{' '}
          <strong data-id="element-611">
            ~{Math.round(totalRecommendedFluid / 1000 * 10) / 10}L
          </strong>
        </span>
      </div>
    </div>;
}
function SegmentRow({
  index,
  name,
  duration,
  state,
  summary,
  isExpanded,
  onClick
}: {
  index: number;
  name: string;
  duration: number;
  state: NutritionState;
  summary: string;
  isExpanded: boolean;
  onClick: () => void;
}) {
  const {
    t
  } = useTranslation();
  const stateConfig = STATE_COLORS[state];
  return <button onClick={onClick} className={`w-full bg-white border p-4 flex items-center gap-4 hover:border-[#FC4C02]/30 transition-colors text-left group shadow-sm ${isExpanded ? 'rounded-t-xl border-[#FC4C02]/20 border-b-0' : 'rounded-xl border-[#E8E8ED]'}`} data-id="element-612">
      <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center text-xs lg:text-sm font-bold flex-shrink-0 transition-colors border ${isExpanded ? 'bg-[#FC4C02] border-[#FC4C02] text-white' : 'bg-[#F7F7FA] border-[#E8E8ED] text-[#6B6B76] group-hover:text-[#FC4C02] group-hover:border-[#FC4C02]/30'}`} data-id="element-613">
        {index}
      </div>

      <div className="flex-1 min-w-0" data-id="element-614">
        <div className="flex items-center justify-between gap-2 mb-1" data-id="element-615">
          <span className="font-bold text-[#2D2D2D] text-sm truncate" data-id="element-616">
            {name}
          </span>
          <div className={`w-2.5 h-2.5 rounded-full ${stateConfig.dot}`} data-id="element-617" />
        </div>
        <div className="flex items-center gap-3 text-xs text-[#9B9BA7]" data-id="element-618">
          <span className="flex items-center gap-1" data-id="element-619">
            <TimerIcon className="w-3 h-3" data-id="element-620" />
            {formatDuration(duration)}
          </span>
          <span className="w-0.5 h-3 bg-[#E8E8ED]" data-id="element-621" />
          <span className={state === 'not_planned' ? 'italic' : 'font-medium text-[#6B6B76]'} data-id="element-622">
            {summary || (state === 'not_planned' ? t('nutrition.tapToPlan') : t('nutrition.noProductsYet'))}
          </span>
        </div>
      </div>

      <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180 text-[#FC4C02]' : 'text-[#D4D4D8] group-hover:text-[#FC4C02]'}`} data-id="element-623">
        <ChevronDownIcon className="w-5 h-5" data-id="element-624" />
      </div>
    </button>;
}
function PackingList({
  products,
  plans
}: {
  products: NutritionProduct[];
  plans: Record<string, {
    intakeItems: NutritionIntakeItem[];
  }>;
}) {
  const {
    t
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const totals = useMemo(() => {
    const map = new Map<string, {
      qty: number;
      cho: number;
      cal: number;
    }>();
    Object.values(plans).forEach(plan => {
      plan.intakeItems.forEach(item => {
        if (item.quantity <= 0) return;
        const current = map.get(item.productId) || {
          qty: 0,
          cho: 0,
          cal: 0
        };
        const product = products.find(p => p.id === item.productId);
        if (product) {
          map.set(item.productId, {
            qty: current.qty + item.quantity,
            cho: current.cho + product.carbsPerUnitG * item.quantity,
            cal: current.cal + product.caloriesPerUnitKcal * item.quantity
          });
        }
      });
    });
    return Array.from(map.entries()).map(([id, stats]) => {
      const product = products.find(p => p.id === id)!;
      return {
        product,
        ...stats
      };
    }).sort((a, b) => b.qty - a.qty);
  }, [products, plans]);
  if (totals.length === 0) return null;
  return <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-625">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between px-5 py-3.5 text-base font-bold text-[#2D2D2D] hover:bg-[#FAFAFA] transition-colors" data-id="element-626">
        <span className="flex items-center gap-2.5" data-id="element-627">
          <ShoppingBagIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-628" />
          {t('nutrition.packingList')}
          <span className="text-xs font-semibold text-[#9B9BA7]" data-id="element-629">
            ({totals.length})
          </span>
        </span>
        {isOpen ? <ChevronUpIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-630" /> : <ChevronDownIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-631" />}
      </button>
      {isOpen && <div className="border-t border-[#E8E8ED] divide-y divide-[#F0F0F5]" data-id="element-632">
          {totals.map(({
        product,
        qty,
        cho,
        cal
      }) => <div key={product.id} className="px-5 py-3 flex items-center justify-between" data-id="element-633">
              <div data-id="element-634">
                <div className="font-medium text-[#2D2D2D] text-sm" data-id="element-635">
                  {product.name}
                </div>
                <div className="text-xs text-[#9B9BA7]" data-id="element-636">
                  Total: {Math.round(cho)}g CHO · {Math.round(cal)} kcal
                </div>
              </div>
              <div className="text-right" data-id="element-637">
                <div className="text-lg font-bold text-[#2D2D2D] tabular-nums leading-none" data-id="element-638">
                  {qty}
                </div>
                <div className="text-[10px] font-medium text-[#9B9BA7] uppercase mt-0.5" data-id="element-639">
                  {product.unitLabel}
                </div>
              </div>
            </div>)}
        </div>}
    </div>;
}
// --- Main Component ---
export function NutritionTable() {
  const {
    t
  } = useTranslation();
  const {
    segments,
    pacingResults,
    nutritionPlans,
    products,
    settings
  } = useRaceStore();
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [segmentBreakdownExpanded, setSegmentBreakdownExpanded] = useState(true);
  const {
    outputs,
    globalState,
    totals
  } = useMemo(() => {
    const outputs: SegmentNutritionOutput[] = [];
    let totalPlannedCho = 0;
    let totalTargetCho = 0;
    let totalPlannedCal = 0;
    let totalTargetCal = 0;
    let totalPlannedSodium = 0;
    let totalTargetSodium = 0;
    let totalRecommendedFluid = 0;
    segments.forEach(segment => {
      const pacing = pacingResults.find(p => p.segmentId === segment.id);
      if (!pacing) return;
      const plan = nutritionPlans[segment.id] || {
        segmentId: segment.id,
        intakeItems: []
      };
      const output = computeSegmentNutrition(segment, pacing, plan, settings, products);
      outputs.push(output);
      totalPlannedCho += output.plannedChoG;
      totalTargetCho += output.targetChoG;
      totalPlannedCal += output.plannedCalKcal;
      totalTargetCal += output.targetCalKcal;
      totalPlannedSodium += output.plannedSodiumMg;
      totalTargetSodium += output.targetSodiumMg;
      totalRecommendedFluid += output.recommendedFluidMl;
    });
    const globalState = computeGlobalNutritionState(outputs);
    return {
      outputs,
      globalState,
      totals: {
        totalPlannedCho,
        totalTargetCho,
        totalPlannedCal,
        totalTargetCal,
        totalPlannedSodium,
        totalTargetSodium,
        totalRecommendedFluid
      }
    };
  }, [segments, pacingResults, nutritionPlans, products, settings]);
  if (segments.length === 0 || pacingResults.length === 0) {
    return <div className="p-12 text-center bg-white rounded-2xl border border-[#E8E8ED] shadow-sm" data-id="element-640">
        <UtensilsIcon className="h-8 w-8 text-[#E8E8ED] mx-auto mb-3" data-id="element-641" />
        <p className="text-[#6B6B76] font-medium" data-id="element-642">{t('nutrition.noPacing')}</p>
      </div>;
  }
  const handleSegmentToggle = (segmentId: string) => {
    setSelectedSegmentId(prev => {
      const next = prev === segmentId ? null : segmentId;
      if (next !== null) {
        const idx = outputs.findIndex(o => o.segmentId === segmentId);
        trackNutritionSegmentOpened(idx);
      }
      return next;
    });
  };
  return <div className="space-y-6 pb-20" data-id="element-643">
      {/* Layer 1: Global Summary */}
      <GlobalSummaryCard state={globalState} {...totals} data-id="element-644" />

      {/* Packing List — right after summary */}
      <PackingList products={products} plans={nutritionPlans} data-id="element-645" />

      {/* Layer 2: Segment Breakdown — Collapsible, open by default */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-646">
        <button onClick={() => setSegmentBreakdownExpanded(!segmentBreakdownExpanded)} className="w-full flex items-center justify-between px-5 py-3.5 text-base font-bold text-[#2D2D2D] hover:bg-[#FAFAFA] transition-colors" data-id="element-647">
          <span className="flex items-center gap-2.5" data-id="element-648">
            <UtensilsIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-649" />
            {t('nutrition.segmentBreakdown')}
          </span>
          {segmentBreakdownExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-650" /> : <ChevronDownIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-651" />}
        </button>
        {segmentBreakdownExpanded && <div className="border-t border-[#E8E8ED] p-4" data-id="element-652">
            <div className="space-y-3" data-id="element-653">
              {outputs.map((output, idx) => {
            const segment = segments.find(s => s.id === output.segmentId)!;
            const pacing = pacingResults.find(p => p.segmentId === output.segmentId)!;
            const isExpanded = selectedSegmentId === output.segmentId;
            return <div key={output.segmentId} data-id="element-654">
                    <SegmentRow index={idx + 1} name={segment.name} duration={pacing.duration} state={output.state} summary={output.productSummary} isExpanded={isExpanded} onClick={() => handleSegmentToggle(output.segmentId)} data-id="element-655" />
                    {isExpanded && <SegmentDetailPanel segmentId={output.segmentId} onClose={() => setSelectedSegmentId(null)} data-id="element-656" />}
                  </div>;
          })}
            </div>
          </div>}
      </div>
    </div>;
}